/**
 * Login form component
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface LoginFormProps {
  onSubmit: (email: string, password: string) => Promise<void>;
  error?: string;
  isLoading?: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, error, isLoading = false }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateEmail = (value: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!validateEmail(email)) {
      setValidationError('Please enter a valid email address.');
      return;
    }

    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters.');
      return;
    }

    await onSubmit(email, password);
  };

  const displayError = validationError || error;

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '420px',
  };

  const headingStyle: React.CSSProperties = {
    fontSize: '1.75rem',
    marginBottom: '8px',
    textAlign: 'center',
  };

  const subheadingStyle: React.CSSProperties = {
    fontSize: '0.95rem',
    color: '#666',
    textAlign: 'center',
    marginBottom: '32px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: 'bold',
    marginBottom: '6px',
    fontSize: '0.95rem',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '1rem',
    border: '2px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
    outline: 'none',
  };

  const fieldGroupStyle: React.CSSProperties = {
    marginBottom: '20px',
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    fontSize: '1rem',
    backgroundColor: isLoading ? '#ccc' : '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    fontWeight: 'bold',
    transition: 'background-color 0.2s',
    marginTop: '8px',
  };

  const errorStyle: React.CSSProperties = {
    padding: '10px 14px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    marginBottom: '20px',
    fontSize: '0.9rem',
  };

  const linkRowStyle: React.CSSProperties = {
    textAlign: 'center',
    marginTop: '24px',
    fontSize: '0.9rem',
    color: '#666',
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <img
            src="/coach_logo.png"
            alt="Coach"
            style={{ maxWidth: '200px', width: '100%' }}
          />
        </div>
        <h1 style={headingStyle}>Welcome Back</h1>
        <p style={subheadingStyle}>Log in to your Coach account</p>

        {displayError && (
          <div style={errorStyle} role="alert">
            {displayError}
          </div>
        )}

        {/* Prominent signup encouragement when login fails */}
        {error && !validationError && (
          <div style={{
            padding: '20px',
            backgroundColor: '#e8f4fd',
            border: '2px solid #b3d9f2',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: '1rem', color: '#1a5276', margin: '0 0 12px 0', fontWeight: 'bold' }}>
              New to Coach?
            </p>
            <p style={{ fontSize: '0.9rem', color: '#2c3e50', margin: '0 0 16px 0' }}>
              Create a free account to start your business analysis in minutes.
            </p>
            <Link
              to="/signup"
              style={{
                display: 'inline-block',
                padding: '10px 28px',
                backgroundColor: '#28a745',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '1rem',
                transition: 'background-color 0.2s',
              }}
            >
              Create Free Account
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div style={fieldGroupStyle}>
            <label htmlFor="email" style={labelStyle}>
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={inputStyle}
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div style={fieldGroupStyle}>
            <label htmlFor="password" style={labelStyle}>
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              style={inputStyle}
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={buttonStyle}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.backgroundColor = '#0056b3';
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.currentTarget.style.backgroundColor = '#007bff';
            }}
          >
            {isLoading ? 'Logging in...' : 'Log In'}
          </button>
        </form>

        <div style={linkRowStyle}>
          Don't have an account?{' '}
          <Link to="/signup" style={{ color: '#007bff', textDecoration: 'none' }}>
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
